// src/services/BillingService.js - WITH DYNAMIC TAX RATES FROM SETTINGS

class BillingService {
  // Generate sequential invoice number from settings
  static async generateInvoiceNumber(restaurantId, StorageService) {
    try {
      const restaurant = await StorageService.getRestaurant(restaurantId);
      const currentNumber = restaurant.invoiceCounter || 1;
      const invoiceNumber = currentNumber.toString().padStart(5, '0');
      
      return {
        invoiceNumber,
        nextNumber: currentNumber + 1
      };
    } catch (error) {
      console.error('Error generating invoice number:', error);
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return {
        invoiceNumber: `${timestamp}-${random}`,
        nextNumber: null
      };
    }
  }

  // Calculate bill with DYNAMIC taxes from restaurant settings
  static calculateBill(orders, restaurant) {
    const allItems = [];
    let subtotal = 0;

    orders.forEach(order => {
      order.itemDetails.forEach(item => {
        const existingItem = allItems.find(i => i.name === item.name && i.price === item.price);
        
        if (existingItem) {
          existingItem.quantity += item.quantity;
          existingItem.amount += item.price * item.quantity;
        } else {
          allItems.push({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            amount: item.price * item.quantity
          });
        }
      });

      subtotal += order.total;
    });

    // Get tax percentages from restaurant settings (defaults to 0 if not registered)
    const cgstPercent = restaurant.cgstPercentage ?? 0;
    const sgstPercent = restaurant.sgstPercentage ?? 0;
    const servicePercent = restaurant.serviceChargePercentage ?? 0;

    // Calculate taxes with dynamic rates
    const cgst = subtotal * (cgstPercent / 100);
    const sgst = subtotal * (sgstPercent / 100);
    const serviceCharge = subtotal * (servicePercent / 100);
    const taxTotal = cgst + sgst + serviceCharge;
    const grandTotal = subtotal + taxTotal;

    return {
      items: allItems,
      subtotal: Math.round(subtotal * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      serviceCharge: Math.round(serviceCharge * 100) / 100,
      taxTotal: Math.round(taxTotal * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100,
      totalItems: allItems.reduce((sum, item) => sum + item.quantity, 0),
      // Store percentages for reference
      cgstPercent,
      sgstPercent,
      servicePercent
    };
  }

  // Create invoice/receipt object with dynamic taxes
  static async createInvoice(orders, restaurant, tableInfo, cashierName = 'System', StorageService) {
    const billData = this.calculateBill(orders, restaurant);
    const { invoiceNumber, nextNumber } = await this.generateInvoiceNumber(restaurant.id, StorageService);
    const now = new Date();

    const invoice = {
      invoiceNumber,
      restaurantId: restaurant.id,
      restaurantName: restaurant.name,
      restaurantAddress: restaurant.address || 'Address not set',
      restaurantPhone: restaurant.phone || 'Phone not set',
      restaurantGSTIN: restaurant.gstNo || restaurant.gstin || 'GSTIN not set',

      tableId: tableInfo.tableId,
      tableName: tableInfo.tableName,
      sessionId: tableInfo.sessionId || null,

      orderIds: orders.map(o => o.id),
      customerName: orders[0]?.customerName || 'Guest',

      date: now.toLocaleDateString('en-IN'),
      time: now.toLocaleTimeString('en-IN'),
      timestamp: now.toISOString(),

      items: billData.items,
      subtotal: billData.subtotal,
      cgst: billData.cgst,
      sgst: billData.sgst,
      serviceCharge: billData.serviceCharge,
      taxTotal: billData.taxTotal,
      grandTotal: billData.grandTotal,
      totalItems: billData.totalItems,

      // Store tax percentages used
      cgstPercent: billData.cgstPercent,
      sgstPercent: billData.sgstPercent,
      servicePercent: billData.servicePercent,

      cashier: cashierName,
      paymentMethod: 'Cash',
      paymentStatus: 'Paid',

      status: 'Generated',
      printed: false,
      cancelled: false,  // New field for cancellation
      cancellationReason: null,  // Reason for cancellation
      cancelledAt: null,  // When it was cancelled
      cancelledBy: null,  // Who cancelled it
      createdAt: now.toISOString(),
      
      _nextInvoiceCounter: nextNumber
    };

    return invoice;
  }

  // Format currency for display
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  // Print receipt
  static printReceipt(invoiceId) {
    const printWindow = window.open(`/print-receipt/${invoiceId}`, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }
}

export default BillingService;